import { CSSProperties, useEffect, useMemo, useState } from "react";
import { DCUIContent, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { transform } from "@iddataweb/idw-common-ui/utils";
import { DCUICaptureStepTypes } from "@enums/dcui-capture-step-types";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIThemingPreviewConfigurationState } from "@idw-types/common/dcui/dcui-theming-preview-configuration-state";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { DCUIThemingFooterContainer } from "./dcui-theming-footer-container/dcui-theming-footer-container";
import { DCUIThemingHeaderContainer } from "./dcui-theming-header-container/dcui-theming-header-container";
import { DCUIThemingMainContainer } from "./dcui-theming-main-container/dcui-theming-main-container";
import { DCUIThemingPreviewConfigurationContainer } from "./dcui-theming-preview-configuration-container/dcui-theming-preview-configuration-container";
import styles from "./dcui-theming-preview-container.less";

type DCUIThemingPreviewContainerProps = {
    currentTab: DCUIThemingTabTypes;
    stepType?: DCUICaptureStepTypes;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
};

export const DCUIThemingPreviewContainer = (props: DCUIThemingPreviewContainerProps) => {
    const {
        currentTab,
        stepType,
        dcuiContent,
        dcuiTheme,
        documentCaptureSteps,
        previewEntityType
    } = props;

    useEffect(() => {
        const cssStyleElementId = "dcui-user-supplied-custom-css";

        if (dcuiTheme.cssStyle) {
            const existingStyle = document.getElementById(cssStyleElementId);
            if (existingStyle) {
                existingStyle.remove();
            }

            const output = transform(dcuiTheme.cssStyle, `#dcui-root-html`, '', false);
            const style = document.createElement('style');
            style.setAttribute("id", cssStyleElementId);
            style.setAttribute("type", "text/css");
            style.appendChild(document.createTextNode(output));
            document.head.appendChild(style);
        } else {
            const existingStyle = document.getElementById(cssStyleElementId);
            if (existingStyle) {
                existingStyle.remove();
            }
        }
    }, [dcuiTheme.cssStyle]);


    const [headerLogoUrl, setHeaderLogoUrl] = useState(dcuiTheme.header.logoUrl ? `${dcuiTheme.header.logoUrl}?_cb=${new Date()}` : "");
    const [previewConfigurationState, setPreviewConfigurationState] = useState<DCUIThemingPreviewConfigurationState>({
        showStartPageInProgress: false,
        showCompletePageRedirectMessage: false,
        showCompletePageUploadErrorMessage: false,
        showCompletePageUploadInProgress: false
    });

    useEffect(() => {
        setHeaderLogoUrl(dcuiTheme.header.logoUrl ? `${dcuiTheme.header.logoUrl}?_cb=${new Date().getTime()}` : "");
    }, [dcuiTheme.header.logoUrl]);

    const contentContainerStyle: CSSProperties = {
        fontFamily: dcuiTheme.general.font
    };

    const showPreviewConfigurationContainer = useMemo(() => {
        return [
            DCUIThemingTabTypes.START_PAGE,
            DCUIThemingTabTypes.CAPTURE_COMPLETE,
          ].includes(currentTab);
    }, [currentTab]);

    return (
        <div className={styles.dcuiThemingPreviewContainer}>
            {showPreviewConfigurationContainer && (
                <DCUIThemingPreviewConfigurationContainer
                    currentTab={currentTab}
                    previewConfigurationState={previewConfigurationState}
                    setPreviewConfigurationState={setPreviewConfigurationState}
                />
            )}
            <div id="dcui-root-html">
                <div>
                    <div id="root">
                        <div id={"content-container"} className={styles.dcuiThemingPreviewApplicationContainer} style={contentContainerStyle}>
                            <DCUIThemingHeaderContainer
                                headerContent={dcuiContent.header}
                                headerTheme={dcuiTheme.header}
                                headerLogoUrl={headerLogoUrl}
                            />
                            <DCUIThemingMainContainer
                                currentTab={currentTab}
                                stepType={stepType}
                                dcuiContent={dcuiContent}
                                dcuiTheme={dcuiTheme}
                                documentCaptureSteps={documentCaptureSteps}
                                previewEntityType={previewEntityType}
                                previewConfigurationState={previewConfigurationState}
                            />
                            <DCUIThemingFooterContainer
                                footerContent={dcuiContent.footer}
                                footerTheme={dcuiTheme.footer}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};